/**
 * Privé Brain — Storefront Components
 * Connects to prive-search.fly.dev for AI-powered product features.
 *
 * Components:
 *   <brain-similar-fragrances>  — Scent-profile matched recommendations
 *   <brain-fragrance-profile>   — Structured notas/familia/concentración
 *   <brain-image-search>        — Camera icon for visual product search
 *   <brain-whatsapp-inquiry>    — WhatsApp product inquiry button
 */

(function() {
  'use strict';

  var API = 'https://prive-search.fly.dev';
  var WA_NUMBER = (document.querySelector('meta[name="prive-wa"]') || {}).content || '';

  // Customer login state — checked from Shopify meta tag (set server-side)
  // and localStorage (set by prive-customer-brain.js after identify).
  function _isLoggedIn() {
    var metaCid = (document.querySelector('meta[name="prive-cid"]') || {}).content || '';
    if (metaCid) return true;
    try { return !!localStorage.getItem('prive_brain_cid'); } catch(e) { return false; }
  }

  // Per-session cache for idempotent GETs. sessionStorage scope (per tab) is
  // the right lifetime: profile/similar responses don't change mid-browse and
  // cheap caching here absorbs 429s when users revisit the same product.
  // POST /api/search/image doesn't use this helper, so it stays uncached.
  function brainFetch(path) {
    var cacheKey = 'prive_brain_cache:' + path;
    try {
      var hit = sessionStorage.getItem(cacheKey);
      if (hit) return Promise.resolve(JSON.parse(hit));
    } catch (e) {}
    return fetch(API + path).then(function(r) {
      if (!r.ok) throw new Error('Brain API ' + r.status);
      return r.json();
    }).then(function(json) {
      try { sessionStorage.setItem(cacheKey, JSON.stringify(json)); } catch (e) {}
      return json;
    });
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text) e.textContent = text;
    return e;
  }

  function svgEl(tag, attrs) {
    var e = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (var k in attrs) if (attrs.hasOwnProperty(k)) e.setAttribute(k, attrs[k]);
    return e;
  }

  function _navArrow(dir) {
    var svg = svgEl('svg', { viewBox: '0 0 24 24', 'aria-hidden': 'true' });
    svg.appendChild(svgEl('path', { d: dir === 'prev' ? 'M15 18 9 12l6-6' : 'm9 18 6-6-6-6' }));
    return svg;
  }

  function fmtPrice(p) {
    return p != null ? 'L ' + Number(p).toLocaleString('es-HN') : '';
  }

  function _intelMatchPayload(match, index) {
    match = match || {};
    var out = { rank: index + 1 };
    if (match.handle) out.handle = String(match.handle);
    if (match.product_id) out.product_id = String(match.product_id);
    if (match.variant_id) out.variant_id = String(match.variant_id);
    if (match.title) out.title = String(match.title).slice(0, 180);
    if (match.vendor) out.vendor = String(match.vendor).slice(0, 120);
    if (match.url) out.url = String(match.url).split('?')[0];
    if (match.price != null) out.price = match.price;
    if (match.compareAtPrice != null) out.compare_at_price = match.compareAtPrice;
    if (typeof match.availableForSale === 'boolean') out.available = match.availableForSale;
    if (match.match_score != null) out.match_score = match.match_score;
    if (match.concentration) out.concentration = String(match.concentration).slice(0, 80);
    if (match.bottle_size) out.bottle_size = String(match.bottle_size).slice(0, 40);
    if (match.gender) out.gender = String(match.gender).slice(0, 40);
    return out;
  }

  function _imageSearchIntelPayload(data, matches, activeIndex, action) {
    matches = matches || [];
    var payload = {
      result_count: matches.length,
      active_rank: (activeIndex || 0) + 1
    };
    if (action) payload.action = action;
    if (data && data.identification) {
      if (typeof data.identification === 'string') payload.identification = data.identification.slice(0, 180);
      else {
        try { payload.identification = JSON.stringify(data.identification).slice(0, 500); } catch (_) {}
      }
    }
    payload.active_match = _intelMatchPayload(matches[activeIndex || 0], activeIndex || 0);
    payload.top_matches = matches.slice(0, 5).map(function(match, i) { return _intelMatchPayload(match, i); });
    return payload;
  }

  // ── Similar Fragrances ──────────────────────────────────────────────

  var SIMILAR_PERSONAL_THRESHOLD = 60;
  var SIMILAR_SAME_VENDOR_CAP = 2;

  function _brainCapability() {
    if (window.PriveBrainIntel
      && /^[A-Za-z0-9_-]{20,160}$/.test(window.PriveBrainIntel._vid || '')
      && /^[a-f0-9]{64}$/i.test(window.PriveBrainIntel._vidToken || '')) {
      return {
        vid: window.PriveBrainIntel._vid,
        vid_token: window.PriveBrainIntel._vidToken,
        handoff_ticket: window.PriveBrainIntel._handoffTicket,
        handoff_expires_at: window.PriveBrainIntel._handoffExpiresAt,
        order_ticket: window.PriveBrainIntel._orderTicket,
        order_expires_at: window.PriveBrainIntel._orderExpiresAt
      };
    }
    var stores = [];
    try { stores.push(localStorage); } catch (e) {}
    try { stores.push(sessionStorage); } catch (e) {}
    for (var i = 0; i < stores.length; i++) {
      try {
        var stored = JSON.parse(stores[i].getItem('prive_brain_capability_v2') || 'null');
        if (stored
          && /^[A-Za-z0-9_-]{20,160}$/.test(stored.vid || '')
          && /^[a-f0-9]{64}$/i.test(stored.vid_token || '')) return stored;
      } catch (e) {}
    }
    return null;
  }

  function _urlWithBrainHandoff(rawUrl) {
    var url = rawUrl;
    var capability = _brainCapability();
    if (!capability
      || !/^[A-Za-z0-9_-]{43}$/.test(capability.handoff_ticket || '')
      || Number(capability.handoff_expires_at) <= Date.now() + 60000) return url;
    var handoffTicket = capability.handoff_ticket;
    try {
      var parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('prive_handoff', handoffTicket);
      return parsed.toString();
    } catch (e) {
      var hashIdx = url.indexOf('#');
      var base = hashIdx === -1 ? url : url.slice(0, hashIdx);
      var fragment = hashIdx === -1 ? '' : url.slice(hashIdx);
      var separator = base.indexOf('?') === -1 ? '?' : '&';
      return base + separator + 'prive_handoff=' + encodeURIComponent(handoffTicket) + fragment;
    }
  }

  function _whatsAppProductHref(title, rawUrl) {
    var msg = encodeURIComponent('Hola, me interesa este producto:\n' + title + '\n' + _urlWithBrainHandoff(rawUrl));
    return 'https://wa.me/' + WA_NUMBER + '?text=' + msg;
  }

  function _cleanProductQuery(title, vendor) {
    var q = String(title || '').replace(/[\r\n]/g, ' ').trim();
    if (q.indexOf('/') !== -1) q = q.split('/')[0];
    q = q.replace(/\b\d+(?:\.\d+)?\s*ml\b/gi, ' ');
    q = q.replace(/\s*\((?:M|W|U)\)\s*/gi, ' ');
    q = q.replace(/\s+/g, ' ').trim();
    var v = String(vendor || '').replace(/[\r\n]/g, ' ').trim();
    if (v && q.toLowerCase().indexOf(v.toLowerCase()) !== 0) q = v + ' ' + q;
    return q;
  }

  function _escapeRegExp(str) {
    return String(str || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function _coreSimilarQuery(title, vendor) {
    var q = String(title || '').replace(/[\r\n]/g, ' ').trim();
    if (q.indexOf('/') !== -1) q = q.split('/')[0];
    var v = String(vendor || '').replace(/[\r\n]/g, ' ').trim();
    if (v) q = q.replace(new RegExp('^\\s*' + _escapeRegExp(v) + '\\b', 'i'), ' ');
    q = q.replace(/\b\d+(?:\.\d+)?\s*ml\b/gi, ' ');
    q = q.replace(/\s*\((?:M|W|U)\)\s*/gi, ' ');
    q = q.replace(/\b(?:eau\s+de\s+toilette|eau\s+de\s+parfum|edt|edp|perfume|parfum|cologne|spray|sp|man|men|woman|women|hombre|mujer|pour\s+homme|pour\s+femme)\b/gi, ' ');
    q = q.replace(/\s+/g, ' ').trim();
    return q;
  }

  function _similarQueries(title, vendor) {
    var clean = _cleanProductQuery(title, vendor);
    var core = _coreSimilarQuery(title, vendor);
    var raw = String(title || '').replace(/[\r\n]/g, ' ').trim();
    var brand = String(vendor || '').replace(/[\r\n]/g, ' ').trim();
    var seen = {};
    var out = [];
    [clean, core, raw, brand].forEach(function(q) {
      q = String(q || '').replace(/\s+/g, ' ').trim();
      if (q.length < 2) return;
      var key = q.toLowerCase();
      if (seen[key]) return;
      seen[key] = true;
      out.push(q);
    });
    return out;
  }

  function brainPostSearch(body) {
    var payload = Object.assign({}, body || {});
    var cacheKey = 'prive_brain_search:' + JSON.stringify(payload);
    try {
      var hit = sessionStorage.getItem(cacheKey);
      if (hit) return Promise.resolve(JSON.parse(hit));
    } catch (e) {}

    var ctrl = new AbortController();
    var to = setTimeout(function() { ctrl.abort(); }, 12000);
    return fetch(API + '/api/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: ctrl.signal,
    }).then(function(r) {
      clearTimeout(to);
      if (!r.ok) throw new Error('Brain API ' + r.status);
      return r.json();
    }).then(function(json) {
      try { sessionStorage.setItem(cacheKey, JSON.stringify(json)); } catch (e) {}
      return json;
    }).catch(function(err) {
      clearTimeout(to);
      throw err;
    });
  }

  function _brainSimilarState(hasResults, count, label) {
    try {
      document.dispatchEvent(new CustomEvent('prive:brain-similar', {
        detail: { hasResults: !!hasResults, count: count || 0, label: label || '' },
      }));
    } catch (e) {}
  }

  function _hideShopifyRecommendationFallback() {
    var nodes = [];
    try { nodes = document.querySelectorAll('product-recommendations[intent="related"]'); } catch (e) { nodes = []; }
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      node.hidden = true;
      node.setAttribute('hidden', 'hidden');
      var section = node.closest ? node.closest('.shopify-section') : null;
      if (section) {
        section.hidden = true;
        section.setAttribute('hidden', 'hidden');
      }
    }
  }

  function _tasteScore(p) {
    var n = p && p.taste_score != null ? Number(p.taste_score) : 0;
    return n === n ? n : 0;
  }

  function _hasPersonalSignal(products) {
    return (products || []).some(function(p) { return _tasteScore(p) >= SIMILAR_PERSONAL_THRESHOLD; });
  }

  function _similarScoreLabel(p) {
    var taste = _tasteScore(p);
    if (taste >= SIMILAR_PERSONAL_THRESHOLD) return 'Tu gusto ' + Math.round(taste) + '%';
    var fit = p && p.fit && Number(p.fit.score);
    if (fit >= 0.85) return 'Muy cercano';
    if (fit >= 0.7) return 'Cercano';
    return '';
  }

  function _hideEmptyJudgeMeReviews() {
    var widgets = [];
    try { widgets = document.querySelectorAll('#judgeme_product_reviews, .jdgm-review-widget, .jdgm-rev-widg'); } catch (e) { widgets = []; }
    for (var i = 0; i < widgets.length; i++) {
      var widget = widgets[i];
      if (!widget) continue;
      var section = widget.closest ? widget.closest('.shopify-section') : null;
      var text = ((widget.textContent || '') + ' ' + (section && section !== widget ? section.textContent || '' : '')).replace(/\s+/g, ' ').trim();
      var countAttr = widget.getAttribute ? widget.getAttribute('data-number-of-reviews') : null;
      var emptyByText = /(?:no ha recibido ninguna reseña|hasn.?t received any reviews yet|no reviews yet)/i.test(text);
      var emptyByData = String(countAttr || '') === '0' || /number_of_reviews[\\"']*\s*:?\s*0\b/i.test(text);
      if (!emptyByText && !emptyByData) continue;
      var target = section || widget;
      target.hidden = true;
      if (target.setAttribute) target.setAttribute('hidden', 'hidden');
      if (target.style) target.style.display = 'none';
    }
  }

  function _installEmptyJudgeMeReviewHider() {
    _hideEmptyJudgeMeReviews();
    [800, 2000, 5000, 10000].forEach(function(ms) { setTimeout(_hideEmptyJudgeMeReviews, ms); });
    try {
      var mo = new MutationObserver(_hideEmptyJudgeMeReviews);
      mo.observe(document.body, { childList: true, subtree: true });
      setTimeout(function() { mo.disconnect(); }, 12000);
    } catch (e) {}
  }

  var BrainSimilar = class extends HTMLElement {
    connectedCallback() {
      if (this._loaded) return;
      this._loaded = true;
      var handle = this.getAttribute('product-handle');
      if (!handle) return;
      var self = this;
      var limit = parseInt(this.getAttribute('limit'), 10) || 6;
      var generation = 0;
      var lastCapabilityKey = null;
      function load() {
        var capability = _brainCapability();
        lastCapabilityKey = capability ? capability.vid + ':' + capability.vid_token : '';
        var currentGeneration = ++generation;
        self.setAttribute('data-state', 'loading');
        self._load(limit)
          .then(function(products) {
            if (currentGeneration !== generation) return;
            if (products.length > 0) {
              self._render(products.slice(0, limit));
              self.setAttribute('data-state', 'ready');
              _hideShopifyRecommendationFallback();
              _brainSimilarState(true, products.length, self._labelFor(products));
            } else {
              self.setAttribute('data-state', 'empty');
              _brainSimilarState(false, 0, '');
            }
          })
          .catch(function(err) {
            if (currentGeneration !== generation) return;
            self.setAttribute('data-state', 'error');
            _brainSimilarState(false, 0, '');
            console.warn('[Brain] similar-fragrances error:', err);
          });
      }
      load();
      this._onBrainCapabilityReady = function() {
        var capability = _brainCapability();
        var key = capability ? capability.vid + ':' + capability.vid_token : '';
        if (key !== lastCapabilityKey) load();
      };
      window.addEventListener('prive:brain-vid-ready', this._onBrainCapabilityReady);
    }

    disconnectedCallback() {
      if (this._onBrainCapabilityReady) {
        window.removeEventListener('prive:brain-vid-ready', this._onBrainCapabilityReady);
        this._onBrainCapabilityReady = null;
      }
    }

    _load(limit) {
      var self = this;
      var title = this.getAttribute('product-title') || '';
      var vendor = this.getAttribute('product-vendor') || '';
      var variantId = this.getAttribute('variant-id') || '';
      var productGid = this.getAttribute('product-gid') || '';
      var sourceHandle = this.getAttribute('product-handle') || '';
      var capability = _brainCapability();
      var queries = _similarQueries(title, vendor);
      var collected = [];
      var seen = {};

      function merge(results) {
        (results || []).forEach(function(p) {
          if (!p || !p.handle || !p.title || !p.url) return;
          if (p.availableForSale === false) return;
          if (sourceHandle && String(p.handle) === String(sourceHandle)) return;
          if (productGid && String(p.id || '') === String(productGid)) return;
          var key = String(p.handle || p.id || p.url);
          if (seen[key]) return;
          seen[key] = true;
          collected.push(p);
        });
      }

      function run(index) {
        if (index >= queries.length) return Promise.resolve(collected);
        var body = {
          q: queries[index],
          limit: Math.max(limit * 3, 12),
        };
        if (variantId) body.history = [variantId];
        if (capability) {
          body.vid = capability.vid;
          body.vid_token = capability.vid_token;
        }
        return brainPostSearch(body).then(function(data) {
          merge(data && data.results);
          return run(index + 1);
        }).catch(function(err) {
          console.warn('[Brain] similar query failed:', err);
          return run(index + 1);
        });
      }

      return run(0).then(function(products) {
        if (products.length > 0 && window.PriveBrainIntel && typeof window.PriveBrainIntel._track === 'function') {
          try {
            window.PriveBrainIntel._track('similar_impression', {
              handle: sourceHandle,
              result_count: products.length,
              mode: self._labelFor(products),
              top_results: products.slice(0, 5).map(function(p, i) { return _intelMatchPayload(p, i); }),
            });
          } catch (e) {}
        }
        return self._rankProducts(products, limit);
      });
    }

    _rankProducts(products, limit) {
      var sourceVendor = String(this.getAttribute('product-vendor') || '').trim().toLowerCase();
      var list = products || [];
      if (!sourceVendor) return list.slice(0, limit);
      var hasOtherVendor = list.some(function(p) { return String((p && p.vendor) || '').trim().toLowerCase() !== sourceVendor; });
      if (!hasOtherVendor) return list.slice(0, limit);
      var out = [];
      var sameVendorCount = 0;
      list.forEach(function(p) {
        if (out.length >= limit || !p) return;
        var isSameVendor = String(p.vendor || '').trim().toLowerCase() === sourceVendor;
        if (isSameVendor) {
          if (sameVendorCount >= SIMILAR_SAME_VENDOR_CAP) return;
          sameVendorCount += 1;
        }
        out.push(p);
      });
      return out;
    }

    _labelFor(products) {
      return _hasPersonalSignal(products) ? 'Elegidas para ti' : 'Fragancias similares';
    }

    _render(products) {
      this.textContent = '';
      var label = this._labelFor(products);
      var section = el('div', 'brain-similar');
      var header = el('div', 'brain-similar__header');
      header.appendChild(el('p', 'brain-similar__eyebrow heading heading--small', label === 'Elegidas para ti' ? 'Según su perfil olfativo' : 'Según este aroma'));
      header.appendChild(el('h2', 'brain-similar__title heading h3', label));
      section.appendChild(header);
      var scroll = el('div', 'brain-similar__scroll');
      var list = el('div', 'brain-similar__list');
      var sourceHandle = this.getAttribute('product-handle');
      products.forEach(function(p) {
        var card = el('a', 'brain-similar__card');
        card.href = p.url;
        card.addEventListener('click', function() {
          if (window.PriveBrainIntel) window.PriveBrainIntel.trackSimilarClick(p.handle, sourceHandle);
        });
        if (p.image) {
          var img = document.createElement('img');
          img.src = p.image; img.alt = p.title || '';
          img.loading = 'lazy'; img.width = 180; img.height = 180;
          img.className = 'brain-similar__image';
          card.appendChild(img);
        }
        var info = el('div', 'brain-similar__info');
        var scoreLabel = _similarScoreLabel(p);
        if (scoreLabel) info.appendChild(el('span', 'brain-similar__match text--xsmall', scoreLabel));
        if (p.vendor) info.appendChild(el('span', 'brain-similar__vendor text--xsmall text--subdued', p.vendor));
        info.appendChild(el('p', 'brain-similar__name text--small', p.title));
        var pw = el('div', 'brain-similar__price');
        if (p.compareAtPrice && p.compareAtPrice > p.price) {
          pw.appendChild(el('span', 'price price--highlight', fmtPrice(p.price)));
          pw.appendChild(el('span', 'price price--compare', fmtPrice(p.compareAtPrice)));
        } else {
          pw.appendChild(el('span', 'price', fmtPrice(p.price)));
        }
        info.appendChild(pw);
        card.appendChild(info);
        list.appendChild(card);
      });
      scroll.appendChild(list);
      section.appendChild(scroll);
      this.appendChild(section);
    }
  };
  customElements.define('brain-similar-fragrances', BrainSimilar);

  // ── Fragrance Profile ───────────────────────────────────────────────

  var BrainProfile = class extends HTMLElement {
    connectedCallback() {
      var self = this;
      var handle = this.getAttribute('product-handle');
      if (!handle) return;
      brainFetch('/api/products/' + handle + '/profile')
        .then(function(data) { if (data.profile) self._render(data.profile); })
        .catch(function(err) { console.warn('[Brain] fragrance-profile error:', err); });
    }
    _render(profile) {
      var hasNotes = profile.notas_salida || profile.notas_corazon || profile.notas_fondo;
      var hasMeta = profile.familia || profile.concentracion;
      if (!hasNotes && !hasMeta) return;
      this.textContent = '';
      var w = el('div', 'brain-profile');
      if (hasMeta) {
        var meta = el('div', 'brain-profile__meta');
        if (profile.familia) meta.appendChild(el('span', 'brain-profile__badge', profile.familia));
        if (profile.concentracion) meta.appendChild(el('span', 'brain-profile__badge brain-profile__badge--accent', profile.concentracion));
        if (profile.genero) {
          var gl = profile.genero === 'M' ? 'Masculino' : profile.genero === 'F' ? 'Femenino' : 'Unisex';
          meta.appendChild(el('span', 'brain-profile__badge brain-profile__badge--subtle', gl));
        }
        w.appendChild(meta);
      }
      if (hasNotes) {
        var pyr = el('div', 'brain-profile__pyramid');
        pyr.appendChild(el('p', 'brain-profile__pyramid-title heading heading--xsmall', 'Pirámide Olfativa'));
        if (profile.notas_salida) pyr.appendChild(this._noteRow('Salida', profile.notas_salida, 'top'));
        if (profile.notas_corazon) pyr.appendChild(this._noteRow('Corazón', profile.notas_corazon, 'heart'));
        if (profile.notas_fondo) pyr.appendChild(this._noteRow('Fondo', profile.notas_fondo, 'base'));
        w.appendChild(pyr);
      }
      this.appendChild(w);
    }
    _noteRow(label, notes, tier) {
      var row = el('div', 'brain-profile__note-row brain-profile__note-row--' + tier);
      row.appendChild(el('span', 'brain-profile__note-label text--xsmall text--subdued', label));
      var str = Array.isArray(notes) ? notes.join(', ') : String(notes);
      row.appendChild(el('span', 'brain-profile__note-values text--small', str));
      return row;
    }
  };
  customElements.define('brain-fragrance-profile', BrainProfile);

  // ── Image Search (Hybrid: OCR-first, Gemini fallback) ──────────────
  //
  // Fast path:  Client-side OCR via Tesseract.js → text search (~2s)
  // Fallback:   Gemini 2.5 Flash vision API → text search (~3s)
  //
  // Most perfume boxes/bottles have visible text (brand + name). OCR
  // extracts it in the browser, sends a text query to /api/search (fast),
  // and results appear instantly. When OCR fails (blurry, no text), Gemini
  // identifies the product from the photo and searches by brand + name.

  // Lazy-loaded Tesseract.js singleton. ~1.5MB, cached by CDN after
  // first load. Created on first camera use, reused within session.
  var _ocrWorker = null;
  var _ocrPromise = null;
  function getOcrWorker() {
    if (_ocrWorker) return Promise.resolve(_ocrWorker);
    if (_ocrPromise) return _ocrPromise;
    _ocrPromise = new Promise(function(resolve, reject) {
      // Load Tesseract.js from CDN (lazy, only when camera is used)
      var script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js';
      script.onload = function() {
        Tesseract.createWorker('eng', 1, { logger: function() {} })
          .then(function(w) { _ocrWorker = w; resolve(w); })
          .catch(function(err) { _ocrPromise = null; reject(err); });
      };
      script.onerror = function() { _ocrPromise = null; reject(new Error('Failed to load OCR library')); };
      document.head.appendChild(script);
    });
    return _ocrPromise;
  }

  // OCR helpers — delegated to the shared window.PriveBrainOcr IIFE
  // (assets/prive-brain-ocr.js) so the same stop list and thresholds are
  // applied here and by the live-camera-search page. Fallback shims are
  // included so a late script load doesn't break image search.
  function normalizeOcr(raw) {
    var impl = window.PriveBrainOcr && window.PriveBrainOcr.normalizeOcr;
    return impl ? impl(raw) : { text: '', tokens: [] };
  }
  function isUsableOcr(tokens) {
    var impl = window.PriveBrainOcr && window.PriveBrainOcr.isUsableOcr;
    return impl ? impl(tokens) : false;
  }

  // Downscale before upload or OCR. Phone photos are 5-12MB; CLIP only
  // needs 224px and OCR works fine at 1280px. Also returns a canvas
  // reference for client-side OCR (avoids re-decoding the image).
  function resizeForUpload(file) {
    return new Promise(function(resolve) {
      if (!/^image\//.test(file.type)) return resolve({ blob: file, canvas: null });
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function() {
        URL.revokeObjectURL(url);
        var maxDim = 1280;
        var w = img.naturalWidth, h = img.naturalHeight;
        var scale = Math.min(1, maxDim / Math.max(w, h));
        var tw = Math.max(1, Math.round(w * scale));
        var th = Math.max(1, Math.round(h * scale));
        var canvas = document.createElement('canvas');
        canvas.width = tw; canvas.height = th;
        canvas.getContext('2d').drawImage(img, 0, 0, tw, th);
        canvas.toBlob(function(blob) {
          resolve({ blob: blob || file, canvas: canvas });
        }, 'image/jpeg', 0.85);
      };
      img.onerror = function() { URL.revokeObjectURL(url); resolve({ blob: file, canvas: null }); };
      img.src = url;
    });
  }

  // ── Image Search — drawer-based scanner + tabbed result ─────────────
  //
  // Single trigger button per element (header has two: mobile + desktop),
  // but the drawer itself is a body-level singleton overlaid on the
  // current page — no navigation. Two screens:
  //   1. Scanner (D1): live camera with pulse reticle, continuous Gemini
  //      vision scan, "Capturar" + "Subir" buttons. Upload path is
  //      OCR-first (Tesseract → text /api/search) with vision fallback.
  //   2. Result (B2): swipe hero across multiple matches, three tabs
  //      (Precio · Calific. · Notas) with PDP-parity rendering of
  //      familia/concentración/género badges, notes pyramid, accord
  //      chips, rating + opinion distribution, clima/ocasión percentages,
  //      duración bar, optional purchase-similarity row, sticky amber CTA.
  //
  // State is held on a module-level singleton so multiple element mounts
  // share one drawer instance.

  var _drawer = null;
  var _drawerScanMs = 1200;
  var _maxMatches = 4;

  // ── Profile helpers (ported from live-camera-search.js) ──
  function _parseList(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try { var a = JSON.parse(raw); return Array.isArray(a) ? a : []; } catch (e) { return []; }
  }
  function _translateGenero(g) {
    if (!g) return '';
    var k = String(g).toUpperCase();
    if (k === 'M' || k === 'MAN' || k === 'MEN') return 'Masculino';
    if (k === 'F' || k === 'WOMAN' || k === 'WOMEN') return 'Femenino';
    if (k === 'U' || k === 'UNISEX') return 'Unisex';
    // Profile gender_target and search-result gender are already in
    // Spanish ("Masculino", "Femenino"), so pass them through.
    return g;
  }
  function _fmtCountK(n) {
    if (!n && n !== 0) return '';
    if (n >= 1000) {
      var k = n / 1000;
      return (k >= 10 ? k.toFixed(0) : k.toFixed(1).replace(/\.0$/, '')) + 'K';
    }
    return String(n);
  }
  // The /profile API exposes vote distributions for *some* products and
  // flat label arrays for others. When votes are absent, derive a
  // best-effort warm/fresh split from the season-label list so the PDP
  // percent UI still has something to render.
  function _climaPctFromSeasons(seasons) {
    if (!seasons || !seasons.length) return null;
    var warm = 0, fresh = 0;
    seasons.forEach(function(s) {
      var k = String(s).toLowerCase();
      if (/primavera|verano|spring|summer/.test(k)) fresh++;
      else if (/oto[ñn]o|invierno|fall|autumn|winter/.test(k)) warm++;
      else if (/todo|all year|year[- ]?round/.test(k)) { warm++; fresh++; }
    });
    var total = warm + fresh;
    if (total <= 0) return null;
    return { warm: Math.round(warm / total * 100), fresh: Math.round(fresh / total * 100) };
  }
  function _ocasionPctFromOccasions(occasions) {
    if (!occasions || !occasions.length) return null;
    var day = 0, night = 0;
    occasions.forEach(function(o) {
      var k = String(o).toLowerCase();
      if (/d[íi]a|day|diurno|trabajo|oficina|deporte|casual/.test(k)) day++;
      else if (/noche|night|evening|cita|rom[áa]ntic|fiesta|salir/.test(k)) night++;
    });
    var total = day + night;
    if (total <= 0) return null;
    return { dia: Math.round(day / total * 100), noche: Math.round(night / total * 100) };
  }

  function _climaPct(votes) {
    if (!votes) return null;
    var fresh = (Number(votes.spring) || 0) + (Number(votes.summer) || 0);
    var warm = (Number(votes.fall) || 0) + (Number(votes.autumn) || 0) + (Number(votes.winter) || 0);
    var total = warm + fresh;
    if (total <= 0) return null;
    return { warm: Math.round(warm / total * 100), fresh: Math.round(fresh / total * 100) };
  }
  function _ocasionPct(votes) {
    if (!votes) return null;
    var day = Number(votes.day) || 0, night = Number(votes.night) || 0;
    var total = day + night;
    if (total <= 0) return null;
    return { dia: Math.round(day / total * 100), noche: Math.round(night / total * 100) };
  }
  function _duracionScore(votes) {
    if (!votes) return 0;
    var love = Number(votes.love_it) || 0, like = Number(votes.like_it) || 0;
    var neu = Number(votes.neutral) || 0, dis = Number(votes.dislike_it) || 0;
    var hate = Number(votes.hate_it) || 0;
    var total = love + like + neu + dis + hate;
    if (total <= 0) return 0;
    return (5 * love + 4 * like + 3 * neu + 2 * dis + 1 * hate) / total;
  }
  function _duracionLabelFromStep(step) {
    return ['POCA','REGULAR','BUENA','LARGA','EXCELENTE'][step] || '';
  }
  function _duracionStep(score) {
    if (score >= 4.5) return 4;
    if (score >= 3.5) return 3;
    if (score >= 2.5) return 2;
    if (score >= 1.5) return 1;
    if (score > 0)    return 0;
    return -1;
  }
  // Map the label-form longevity (e.g. "Larga") to a 0-4 step. The
  // profile API has two coexisting shapes — vote distributions for some
  // products and label strings for others — so we check both.
  function _longevityStepFromLabel(label) {
    if (!label) return -1;
    var k = String(label).toLowerCase();
    if (/excelen|excellent|eternal/.test(k)) return 4;
    if (/larga|long-?lasting/.test(k)) return 3;
    if (/buena|good|moderada|moderate/.test(k)) return 2;
    if (/regular|weak/.test(k)) return 1;
    if (/poca|short|fleeting/.test(k)) return 0;
    return -1;
  }
  function _prettyHandle(h) {
    return String(h || '').replace(/-/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); });
  }
  // Pull concentration tag from a Privé title like "Armaf Club de Nuit
  // Intense Man EDT (M) / 105 ml". The /profile endpoint doesn't return
  // a concentration field on the English-keyed shape, so we fall back to
  // the title.
  function _concentrationFromTitle(title) {
    if (!title) return '';
    var t = String(title);
    if (/\bEDP\b/i.test(t)) return /\bIntense\b/i.test(t) ? 'EDP Intense' : 'Eau de Parfum';
    if (/\bEDT\b/i.test(t)) return 'Eau de Toilette';
    if (/\bEDC\b/i.test(t)) return 'Eau de Cologne';
    if (/\bExtrait\b/i.test(t)) return 'Extrait de Parfum';
    if (/\bParfum\b/i.test(t)) return 'Parfum';
    return '';
  }
  function _bottleSizeFromTitle(title) {
    if (!title) return '';
    var m = String(title).match(/(\d+)\s*ml\b/i);
    return m ? (m[1] + ' ml') : '';
  }
  // Strip vendor prefix and the trailing "EDT (M) / 105 ml" tail so the
  // hero title shows the clean fragrance name.
  function _cleanTitle(title, vendor) {
    if (!title) return '';
    var t = String(title);
    if (vendor) {
      var v = String(vendor);
      if (t.toLowerCase().indexOf(v.toLowerCase()) === 0) {
        t = t.substring(v.length).replace(/^[\s\-·]+/, '');
      }
    }
    t = t.replace(/\s+(EDP|EDT|EDC|Parfum|Extrait)(\s+Intense)?\s*(\([MFU]\))?\s*\/?\s*\d+\s*ml\s*$/i, '');
    t = t.replace(/\s*\/\s*\d+\s*ml\s*$/i, '');
    t = t.replace(/\s*\([MFU]\)\s*$/i, '');
    return t.trim();
  }

  // ── Block builders (Refined Flow visual language) ──
  function _accentLabel(text) {
    var row = el('div', 'bisd__accent-label');
    row.appendChild(el('span', 'bisd__accent-bar'));
    row.appendChild(el('span', 'bisd__accent-text', text));
    return row;
  }
  function _starRow(value) {
    var v = Math.max(0, Math.min(5, Number(value) || 0));
    var row = el('div', 'bisd__stars');
    for (var i = 1; i <= 5; i++) {
      var filled = v >= i - 0.5;
      var s = svgEl('svg', { viewBox: '0 0 24 24', class: 'bisd__star ' + (filled ? 'is-full' : 'is-empty') });
      s.appendChild(svgEl('polygon', { points: '12 2 15 9 22 10 17 15 18 22 12 18 6 22 7 15 2 10 9 9' }));
      row.appendChild(s);
    }
    return row;
  }
  // Free-shipping threshold (HNL) is read from a meta tag the theme
  // emits — defaults to absent, in which case we never show the badge.
  // Lets the store owner toggle it through shop metafields without a
  // theme deploy.
  function _freeShippingThreshold() {
    var m = document.querySelector('meta[name="prive-free-shipping-threshold"]');
    if (!m) return null;
    var n = parseFloat(m.content);
    return isNaN(n) ? null : n;
  }
  function _priceBlock(match) {
    // Price/compareAtPrice/availableForSale arrive server-side hydrated
    // from Shopify Admin API (prive-search). Values are in HNL.
    var price = match.price;
    var compareAt = match.compareAtPrice;
    var available = (typeof match.availableForSale === 'boolean')
      ? match.availableForSale
      : !(match.inventory != null && match.inventory <= 0);
    var block = el('div', 'bisd__block bisd__block--price');
    var row = el('div', 'bisd__price-row');
    if (price != null) row.appendChild(el('div', 'bisd__price', fmtPrice(price)));
    if (compareAt && compareAt > price) {
      row.appendChild(el('span', 'bisd__price-compare', fmtPrice(compareAt)));
    }
    row.appendChild(el('span', 'bisd__chip bisd__chip--' + (available ? 'stock' : 'out'), available ? 'Disponible' : 'Agotado'));
    var threshold = _freeShippingThreshold();
    if (available && threshold != null && price != null && price >= threshold) {
      row.appendChild(el('span', 'bisd__chip bisd__chip--ship', 'Envío gratis'));
    }
    block.appendChild(row);
    return block;
  }
  function _specGrid(match, profile) {
    var p = (profile && profile.profile) || {};
    var rows = [];
    // Familia: Spanish-keyed shape uses `familia`; English-keyed shape
    // uses `family` + optional `subfamily`.
    var fam = p.familia;
    if (!fam && p.family) {
      fam = p.subfamily ? (p.family + ' ' + p.subfamily) : p.family;
    }
    if (fam) rows.push(['FAMILIA', String(fam).toUpperCase()]);
    // Concentración: Spanish-keyed shape only. Title fallback covers
    // the English-keyed shape (where the field is absent).
    var conc = p.concentracion || (match && _concentrationFromTitle(match.title));
    if (conc) rows.push(['CONCENTRACIÓN', String(conc).toUpperCase()]);
    var year = p.lanzamiento || p.year;
    if (year) rows.push(['LANZAMIENTO', String(year)]);
    var generoLabel = _translateGenero(p.genero || p.gender_target || (match && match.gender));
    if (generoLabel) rows.push(['PARA', generoLabel.toUpperCase()]);
    if (!rows.length) return null;
    var grid = el('div', 'bisd__spec-grid');
    rows.forEach(function(pair) {
      var cell = el('div', 'bisd__spec-cell');
      cell.appendChild(el('div', 'bisd__spec-key', pair[0]));
      cell.appendChild(el('div', 'bisd__spec-val', pair[1]));
      grid.appendChild(cell);
    });
    return grid;
  }

  // Teaser removed — no taste score = no element. Dead space > fake CTA.

  // ── PERFIL tab — visitor taste intelligence ──
  function _profileTab(match) {
    var wrap = el('div', 'bisd__profile');
    var loggedIn = _isLoggedIn();

    // ── Logged in: show taste score + olfactory profile ──
    if (loggedIn) {
      // TU GUSTO for the scanned product — compact inline score
      var tasteScore = match && match.taste_score;
      if (tasteScore && Number(tasteScore) >= SIMILAR_PERSONAL_THRESHOLD) {
        var hero = el('div', 'bisd__profile-hero');
        var heroLabel = el('div', 'bisd__profile-hero-row');
        heroLabel.appendChild(_accentLabel('Tu gusto por esta fragancia'));
        var badge = el('span', 'bisd__taste-badge');
        badge.textContent = Math.round(tasteScore) + '%';
        heroLabel.appendChild(badge);
        hero.appendChild(heroLabel);
        // Thin bar gauge — compact, doesn't eat vertical space
        var barWrap = el('div', 'bisd__taste-bar-wrap');
        var barFill = el('div', 'bisd__taste-bar-fill');
        barFill.style.width = Math.round(tasteScore) + '%';
        barWrap.appendChild(barFill);
        hero.appendChild(barWrap);
        wrap.appendChild(hero);
      }

      // Taste profile section — fetch from search API
      var capability = _brainCapability();
      var profileSection = el('div', 'bisd__profile-section');
      profileSection.appendChild(_accentLabel('Tu perfil olfativo'));
      var profileBody = el('div', 'bisd__profile-body');
      profileBody.appendChild(el('div', 'bisd__tab-loading', 'Cargando…'));
      profileSection.appendChild(profileBody);
      wrap.appendChild(profileSection);

      if (capability) {
        fetch(API + '/api/taste-profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Prive-VID-Token': capability.vid_token },
          body: JSON.stringify({ vid: capability.vid, vid_token: capability.vid_token }),
        }).then(function(r) { return r.json(); }).then(function(data) {
          profileBody.textContent = '';
          if (data && data.has_data) {
            _renderTasteProfile(profileBody, data);
          } else {
            profileBody.appendChild(el('div', 'bisd__profile-empty', 'Aún estamos conociendo tus gustos. Sigue explorando.'));
          }
        }).catch(function() {
          profileBody.textContent = '';
          profileBody.appendChild(el('div', 'bisd__profile-empty', 'No pudimos cargar tu perfil.'));
        });
      } else {
        profileBody.textContent = '';
        profileBody.appendChild(el('div', 'bisd__profile-empty', 'Aún estamos conociendo tus gustos. Sigue explorando.'));
      }

    // ── Anonymous: login CTA ──
    } else {
      var gateSection = el('div', 'bisd__profile-section');
      gateSection.appendChild(_accentLabel('Tu perfil olfativo'));
      var gateBody = el('div', 'bisd__profile-body');
      _renderLoginCta(gateBody);
      gateSection.appendChild(gateBody);
      wrap.appendChild(gateSection);
    }

    return wrap;
  }

  // Render visitor's taste profile data
  function _renderTasteProfile(container, data) {
    // Family affinity bars
    if (data.families && data.families.length > 0) {
      var famWrap = el('div', 'bisd__profile-families');
      data.families.forEach(function(f) {
        var label = f.name.replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); });
        var row = el('div', 'bisd__profile-fam-row');
        row.appendChild(el('span', 'bisd__profile-fam-name', label));
        var track = el('div', 'bisd__profile-fam-track');
        var fill = el('div', 'bisd__profile-fam-fill');
        fill.style.width = f.pct + '%';
        track.appendChild(fill);
        row.appendChild(track);
        row.appendChild(el('span', 'bisd__profile-fam-pct', f.pct + '%'));
        famWrap.appendChild(row);
      });
      container.appendChild(famWrap);
    }

    // Top brands chips
    if (data.top_vendors && data.top_vendors.length > 0) {
      var brandWrap = el('div', 'bisd__profile-brands');
      brandWrap.appendChild(el('div', 'bisd__profile-brands-label', 'Tus marcas'));
      var chips = el('div', 'bisd__profile-brand-chips');
      data.top_vendors.slice(0, 5).forEach(function(v) {
        var name = v.name.replace(/\b\w/g, function(c) { return c.toUpperCase(); });
        chips.appendChild(el('span', 'bisd__profile-brand-chip', name));
      });
      brandWrap.appendChild(chips);
      container.appendChild(brandWrap);
    }

    // Signal count
    if (data.event_count > 0) {
      var signal = el('div', 'bisd__profile-signal');
      signal.textContent = data.event_count + ' interacciones · ' +
        (data.has_purchases ? 'Cliente verificado' : 'Navegante activo');
      container.appendChild(signal);
    }
  }

  // Login CTA for anonymous / no-data visitors
  function _renderLoginCta(container) {
    // Build return URL so Shopify redirects back to the same page after login
    var returnUrl = window.location.pathname + window.location.search;
    var loginUrl = '/account/login?checkout_url=' + encodeURIComponent(returnUrl);
    var cta = el('div', 'bisd__profile-cta');
    cta.innerHTML =
      '<div class="bisd__profile-cta-icon">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
          '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>' +
          '<circle cx="12" cy="7" r="4"/>' +
        '</svg>' +
      '</div>' +
      '<div class="bisd__profile-cta-text">Inicia sesión para descubrir qué fragancias van contigo</div>' +
      '<a href="' + loginUrl + '" class="bisd__profile-cta-btn">Iniciar sesión</a>';
    container.appendChild(cta);
  }

  function _ratingBlock(match, profile) {
    var rating = (match && match.rating > 0) ? match.rating
                 : (profile && profile.rating > 0 ? profile.rating : 0);
    var count = (match && match.rating_count) || (profile && profile.rating_count) || 0;
    if (!rating) return null;
    var block = el('div', 'bisd__block');
    block.appendChild(_accentLabel('Calificación'));
    var inner = el('div', 'bisd__rating-row');
    inner.appendChild(_starRow(rating));
    inner.appendChild(el('span', 'bisd__rating-value', rating.toFixed(2)));
    inner.appendChild(el('span', 'bisd__rating-count', _fmtCountK(count) + ' opiniones'));
    block.appendChild(inner);
    return block;
  }
  function _opinionDistribution(profile) {
    var hist = profile && (profile.rating_histogram || profile.rating_distribution);
    if (!hist) return null;
    var counts = [hist['5'], hist['4'], hist['3'], hist['2'], hist['1']];
    var total = counts.reduce(function(a, b) { return a + (Number(b) || 0); }, 0);
    if (!total) return null;
    var block = el('div', 'bisd__block');
    block.appendChild(el('div', 'bisd__dist-caption', total + ' opiniones · distribución'));
    [5, 4, 3, 2, 1].forEach(function(n, idx) {
      var c = Number(counts[idx]) || 0;
      var pct = Math.round(c / total * 100);
      var line = el('div', 'bisd__dist-line');
      line.appendChild(el('span', 'bisd__dist-num', n + '★'));
      var barWrap = el('div', 'bisd__dist-track');
      var fill = el('div', 'bisd__dist-fill');
      fill.style.width = pct + '%';
      barWrap.appendChild(fill);
      line.appendChild(barWrap);
      line.appendChild(el('span', 'bisd__dist-pct', pct + '%'));
      block.appendChild(line);
    });
    return block;
  }
  // Cálido / Fresco icons match the PDP's fragrance-data snippet:
  // thermometer-with-bulb for warm, snowflake-cross for fresh.
  function _warmIcon() {
    var svg = svgEl('svg', { viewBox: '0 0 24 24', class: 'bisd__pct-icon' });
    svg.appendChild(svgEl('path', { d: 'M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z' }));
    return svg;
  }
  function _freshIcon() {
    var svg = svgEl('svg', { viewBox: '0 0 24 24', class: 'bisd__pct-icon' });
    svg.appendChild(svgEl('line', { x1: '2', y1: '12', x2: '22', y2: '12' }));
    svg.appendChild(svgEl('line', { x1: '12', y1: '2', x2: '12', y2: '22' }));
    svg.appendChild(svgEl('path', { d: 'm20 16-4-4 4-4' }));
    svg.appendChild(svgEl('path', { d: 'm4 8 4 4-4 4' }));
    svg.appendChild(svgEl('path', { d: 'm16 4-4 4-4-4' }));
    svg.appendChild(svgEl('path', { d: 'm8 20 4-4 4 4' }));
    return svg;
  }
  function _sunIcon() {
    var svg = svgEl('svg', { viewBox: '0 0 24 24', class: 'bisd__pct-icon' });
    svg.appendChild(svgEl('circle', { cx: '12', cy: '12', r: '4' }));
    svg.appendChild(svgEl('line', { x1: '12', y1: '2', x2: '12', y2: '5' }));
    svg.appendChild(svgEl('line', { x1: '12', y1: '19', x2: '12', y2: '22' }));
    svg.appendChild(svgEl('line', { x1: '4.22', y1: '4.22', x2: '6.34', y2: '6.34' }));
    svg.appendChild(svgEl('line', { x1: '17.66', y1: '17.66', x2: '19.78', y2: '19.78' }));
    svg.appendChild(svgEl('line', { x1: '2', y1: '12', x2: '5', y2: '12' }));
    svg.appendChild(svgEl('line', { x1: '19', y1: '12', x2: '22', y2: '12' }));
    svg.appendChild(svgEl('line', { x1: '4.22', y1: '19.78', x2: '6.34', y2: '17.66' }));
    svg.appendChild(svgEl('line', { x1: '17.66', y1: '6.34', x2: '19.78', y2: '4.22' }));
    return svg;
  }
  function _moonIcon() {
    var svg = svgEl('svg', { viewBox: '0 0 24 24', class: 'bisd__pct-icon' });
    svg.appendChild(svgEl('path', { d: 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z' }));
    return svg;
  }
  function _pctPair(label, leftIcon, leftVal, leftName, rightIcon, rightVal, rightName) {
    var block = el('div', 'bisd__block');
    block.appendChild(_accentLabel(label));
    var row = el('div', 'bisd__pct-row');
    var l = el('div', 'bisd__pct-item');
    l.appendChild(leftIcon);
    l.appendChild(el('span', 'bisd__pct-val', leftVal + '%'));
    l.appendChild(el('span', 'bisd__pct-name', leftName));
    row.appendChild(l);
    var r = el('div', 'bisd__pct-item');
    r.appendChild(rightIcon);
    r.appendChild(el('span', 'bisd__pct-val', rightVal + '%'));
    r.appendChild(el('span', 'bisd__pct-name', rightName));
    row.appendChild(r);
    block.appendChild(row);
    return block;
  }

  function _climaBlock(profile) {
    // Prefer real vote distribution; fall back to deriving warm/fresh
    // from the season chip list so the percent UI still renders for
    // products whose votes haven't been backfilled.
    var c = _climaPct(profile && profile.season_votes);
    if (!c) {
      var p = (profile && profile.profile) || {};
      c = _climaPctFromSeasons(_parseList(p.seasons));
    }
    if (!c) return null;
    return _pctPair('Clima', _warmIcon(), c.warm, 'Cálido', _freshIcon(), c.fresh, 'Fresco');
  }
  function _ocasionBlock(profile) {
    var o = _ocasionPct(profile && profile.time_of_day_votes);
    if (!o) {
      var p = (profile && profile.profile) || {};
      o = _ocasionPctFromOccasions(_parseList(p.occasions));
    }
    if (!o) return null;
    return _pctPair('Ocasión', _sunIcon(), o.dia, 'Día', _moonIcon(), o.noche, 'Noche');
  }
  function _duracionBlock(profile) {
    // Vote-derived score (preferred when present) → 0-4 step.
    var score = _duracionScore(profile && profile.longevity_votes);
    var step = _duracionStep(score);
    // Fallback: profile.longevity is a label string.
    if (step < 0) {
      var p = (profile && profile.profile) || {};
      step = _longevityStepFromLabel(p.longevity);
    }
    if (step < 0) return null;
    var block = el('div', 'bisd__block');
    block.appendChild(_accentLabel('Duración'));
    var bars = el('div', 'bisd__dur-bars');
    for (var i = 0; i < 5; i++) {
      var seg = el('div', 'bisd__dur-seg' + (i === step ? ' is-active' : ''));
      bars.appendChild(seg);
    }
    block.appendChild(bars);
    var labels = el('div', 'bisd__dur-labels');
    ['POCA', 'REGULAR', 'BUENA', 'LARGA', 'EXCELENTE'].forEach(function(text, i) {
      labels.appendChild(el('span', 'bisd__dur-lbl' + (i === step ? ' is-active' : ''), text));
    });
    block.appendChild(labels);
    return block;
  }
  function _sillageBlock(profile) {
    var p = (profile && profile.profile) || {};
    if (!p.sillage) return null;
    var block = el('div', 'bisd__block');
    block.appendChild(_accentLabel('Estela'));
    block.appendChild(el('div', 'bisd__sillage', String(p.sillage)));
    return block;
  }
  function _notasBlock(profile) {
    var p = (profile && profile.profile) || {};
    var rows = [
      ['SALIDA', _parseList(p.notas_salida || p.notes_top)],
      ['CORAZÓN', _parseList(p.notas_corazon || p.notes_mid)],
      ['FONDO', _parseList(p.notas_fondo || p.notes_base)],
    ];
    if (!rows.some(function(r) { return r[1].length > 0; })) return null;
    var block = el('div', 'bisd__block');
    block.appendChild(_accentLabel('Notas'));
    var pyr = el('div', 'bisd__pyramid');
    rows.forEach(function(pair) {
      if (!pair[1].length) return;
      var row = el('div', 'bisd__pyramid-row');
      row.appendChild(el('span', 'bisd__pyramid-key', pair[0]));
      var chips = el('div', 'bisd__pyramid-chips');
      pair[1].forEach(function(n) { chips.appendChild(el('span', 'bisd__chip-note', n)); });
      row.appendChild(chips);
      pyr.appendChild(row);
    });
    block.appendChild(pyr);
    return block;
  }
  function _acordesBlock(profile) {
    var p = (profile && profile.profile) || {};
    var accords = _parseList(p.main_accords);
    // Fallback: derive accord chips from the family/subfamily pair when
    // explicit main_accords aren't yet exposed by the API (PRV-243).
    if (!accords.length && (p.family || p.familia)) {
      accords = [];
      if (p.familia) accords.push(p.familia);
      if (p.family) accords.push(p.family);
      if (p.subfamily) accords.push(p.subfamily);
    }
    if (!accords.length) return null;
    var block = el('div', 'bisd__block');
    block.appendChild(_accentLabel('Acordes'));
    var row = el('div', 'bisd__chip-row');
    accords.forEach(function(a) { row.appendChild(el('span', 'bisd__chip-accord', String(a).toUpperCase())); });
    block.appendChild(row);
    return block;
  }
  function _purchaseBlock(scores) {
    if (!scores || !scores.length) return null;
    var top = scores.filter(function(s) { return typeof s.score === 'number' && s.score >= 0.5; }).slice(0, 4);
    if (!top.length) return null;
    var block = el('div', 'bisd__block');
    block.appendChild(_accentLabel('Similar a lo que compraste'));
    var row = el('div', 'bisd__purchase-row');
    top.forEach(function(s) {
      var pct = Math.round(s.score * 100);
      var chip = el('a', 'bisd__purchase-chip');
      chip.href = 'https://priveperfumes.com/products/' + s.handle;
      chip.appendChild(el('span', 'bisd__purchase-score', pct + '%'));
      chip.appendChild(el('span', 'bisd__purchase-name', _prettyHandle(s.handle)));
      row.appendChild(chip);
    });
    block.appendChild(row);
    return block;
  }

  function _appendIf(parent, node) { if (node) parent.appendChild(node); }

  // ── Profile adapter ──
  // The search API returns Shopify-hydrated profile data (camelCase from
  // the custom.fragrance_data metaobject). The render functions expect the
  // legacy brain DB snake_case shape. This adapter bridges the two without
  // rewriting any render code.
  function _normalizeProfile(match) {
    var p = match && match.profile;
    if (!p) return null;
    return {
      rating: p.rating || 0,
      rating_count: p.totalVotes || 0,
      rating_histogram: null, // not available from Shopify metaobject
      season_votes: p.seasonVotes || null,
      time_of_day_votes: p.timeOfDayVotes || null,
      longevity_votes: p.longevityVotes || null,
      profile: {
        familia: p.familia || null,
        subfamily: p.subfamily || null,
        concentracion: p.concentracion || null,
        genero: p.genero || null,
        notas_salida: p.topNotes || [],
        notes_top: p.topNotes || [],
        notas_corazon: p.heartNotes || [],
        notes_mid: p.heartNotes || [],
        notas_fondo: p.baseNotes || [],
        notes_base: p.baseNotes || [],
        main_accords: p.mainAccords || []
        // sillage/longevity/seasons/occasions not in Shopify metaobject;
        // render functions have label-driven fallbacks when null
      }
    };
  }

  // ── Drawer factory ──
  function _buildDrawer() {
    var d = {
      mode: 'idle',
      stream: null,
      scanTimer: null,
      inflight: false,
      uploadMode: false,
      lastScrollY: 0,
      profileCache: {},
      matches: [],
      activeMatch: 0,
      activeTab: 0,
      pointerStart: null,
      heroOffset: 0,
      _searchToken: 0,
    };

    var root = el('div', 'brain-image-search-drawer');
    root.setAttribute('data-open', 'false');
    root.setAttribute('aria-hidden', 'true');

    var backdrop = el('div', 'bisd__backdrop');
    backdrop.addEventListener('click', function() { d.hide(); });
    root.appendChild(backdrop);

    var sheet = el('div', 'bisd__sheet');
    sheet.addEventListener('click', function(e) { e.stopPropagation(); });
    root.appendChild(sheet);

    // Camera input (for scan button — opens camera directly)
    var fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.capture = 'environment';
    fileInput.style.display = 'none';
    fileInput.addEventListener('change', function() {
      if (fileInput.files && fileInput.files[0]) {
        var f = fileInput.files[0];
        fileInput.value = '';
        d._handleUpload(f);
      }
    });
    sheet.appendChild(fileInput);

    // Gallery input (for upload button — opens photo picker, no camera)
    var galleryInput = document.createElement('input');
    galleryInput.type = 'file';
    galleryInput.accept = 'image/*';
    galleryInput.style.display = 'none';
    galleryInput.addEventListener('change', function() {
      if (galleryInput.files && galleryInput.files[0]) {
        var f = galleryInput.files[0];
        galleryInput.value = '';
        d._handleUpload(f);
      }
    });
    sheet.appendChild(galleryInput);

    document.body.appendChild(root);
    d.root = root;
    d.sheet = sheet;
    d.fileInput = fileInput;
    d.galleryInput = galleryInput;

    // ── Show / hide ──
    d.show = function() {
      if (root.getAttribute('data-open') === 'true') return;
      d.lastScrollY = window.scrollY || document.documentElement.scrollTop || 0;
      document.documentElement.classList.add('bisd-locked');
      document.body.style.top = '-' + d.lastScrollY + 'px';
      root.setAttribute('data-open', 'true');
      root.setAttribute('aria-hidden', 'false');
      d._renderScanner();
      // Restore last scan result from sessionStorage (survives login redirect)
      var restored = null;
      try { restored = sessionStorage.getItem('prive_scan_result'); } catch(e) {}
      if (restored) {
        try {
          var saved = JSON.parse(restored);
          if (saved.matches && saved.matches.length) {
            d._onMatches(saved);
            try { sessionStorage.removeItem('prive_scan_result'); } catch(e) {}
            return;
          }
        } catch(e) {}
      }
      d._setMode('scanner');
      d._linkCustomer();
      d._tryStartCamera();
    };
    d.hide = function() {
      if (root.getAttribute('data-open') !== 'true') return;
      d._stopCamera();
      if (d._disposeHeroSwipe) d._disposeHeroSwipe();
      root.setAttribute('data-open', 'false');
      root.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('bisd-locked');
      document.body.style.top = '';
      window.scrollTo(0, d.lastScrollY);
    };
    d._setMode = function(mode) {
      d.mode = mode;
      sheet.setAttribute('data-mode', mode);
      var s = sheet.querySelector('.bisd__scanner');
      var r = sheet.querySelector('.bisd__result');
      if (s) s.hidden = mode !== 'scanner';
      if (r) r.hidden = mode !== 'result';
    };

    d._linkCustomer = function() {
      var meta = document.querySelector('meta[name="prive-cid"]');
      var cid = meta && meta.content;
      if (cid && window.PriveBrainIntel && typeof window.PriveBrainIntel.linkCustomer === 'function') {
        try { window.PriveBrainIntel.linkCustomer(String(cid)); } catch (e) {}
      }
    };

    // ── Scanner screen (D1) ──
    d._renderScanner = function() {
      if (sheet.querySelector('.bisd__scanner')) return;
      var pane = el('div', 'bisd__scanner');

      var topRow = el('div', 'bisd__scanner-top');
      var closeBtn = el('button', 'bisd__close');
      closeBtn.type = 'button';
      closeBtn.setAttribute('aria-label', 'Cerrar');
      var closeSvg = svgEl('svg', { viewBox: '0 0 24 24', 'aria-hidden': 'true' });
      closeSvg.appendChild(svgEl('path', { d: 'M18 6 6 18M6 6l12 12' }));
      closeBtn.appendChild(closeSvg);
      closeBtn.addEventListener('click', function() { d.hide(); });
      topRow.appendChild(closeBtn);
      pane.appendChild(topRow);

      var progressTrack = el('div', 'bisd__progress');
      progressTrack.appendChild(el('div', 'bisd__progress-fill'));
      pane.appendChild(progressTrack);

      var heading = el('div', 'bisd__heading');
      heading.appendChild(el('div', 'bisd__heading-title', '¿Qué perfume es?'));
      heading.appendChild(el('div', 'bisd__heading-sub', 'apunta · toca · listo'));
      pane.appendChild(heading);

      var viewport = el('div', 'bisd__viewport');
      var video = document.createElement('video');
      video.setAttribute('autoplay', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      video.muted = true;
      viewport.appendChild(video);
      var canvas = document.createElement('canvas');
      canvas.className = 'bisd__canvas';
      canvas.width = 640; canvas.height = 480;
      viewport.appendChild(canvas);
      var grid = el('div', 'bisd__grid');
      viewport.appendChild(grid);
      var reticle = el('div', 'bisd__reticle');
      viewport.appendChild(reticle);
      // Scanning feedback overlay — frosted pill with pulsing dot
      var scanFeedback = el('div', 'bisd__scan-feedback');
      scanFeedback.appendChild(el('div', 'bisd__scan-dot'));
      scanFeedback.appendChild(el('div', 'bisd__scan-feedback-text', 'Analizando'));
      viewport.appendChild(scanFeedback);
      var caption = el('div', 'bisd__viewport-caption', 'toca para escanear');
      viewport.appendChild(caption);
      d._caption = caption;
      // Perm panel is a fallback shown only when getUserMedia rejects.
      // Hidden by default — the browser's native permission prompt is the
      // primary affordance, and showing both at once was confusing.
      var permState = el('div', 'bisd__perm');
      permState.hidden = true;
      var permTitle = el('div', 'bisd__perm-title', 'Cámara no disponible');
      var permMsg = el('div', 'bisd__perm-msg', 'No se pudo abrir la cámara. Usa el botón "Subir" para enviar una foto.');
      permState.appendChild(permTitle);
      permState.appendChild(permMsg);
      var permBtn = el('button', 'bisd__perm-btn');
      permBtn.type = 'button';
      permBtn.textContent = 'Reintentar';
      permBtn.addEventListener('click', function() { d._tryStartCamera(true); });
      permState.appendChild(permBtn);
      viewport.appendChild(permState);
      pane.appendChild(viewport);

      // Tap overlay: invisible tap target over the viewport
      var tapOverlay = el('div', 'bisd__tap-overlay');
      tapOverlay.addEventListener('click', function() {
        if (!d.stream) {
          if (d._cameraStarting) { d._showToast('Esperando cámara…'); return; }
          d._tryStartCamera(true);
          return;
        }
        if (!d.inflight) d._captureFrame(true);
      });
      viewport.appendChild(tapOverlay);

      // Capture flash overlay — brief white flash on tap
      var flash = el('div', 'bisd__flash');
      viewport.appendChild(flash);

      // Bracket corners for aim guide + visual feedback
      var c1 = el('div', 'bisd__corner bisd__corner--tl');
      var c2 = el('div', 'bisd__corner bisd__corner--tr');
      var c3 = el('div', 'bisd__corner bisd__corner--bl');
      var c4 = el('div', 'bisd__corner bisd__corner--br');
      viewport.appendChild(c1);
      viewport.appendChild(c2);
      viewport.appendChild(c3);
      viewport.appendChild(c4);
      d._brackets = [c1, c2, c3, c4];

      // Upload button with icon + text in top row
      var uploadBtn = el('button', 'bisd__upload-link');
      uploadBtn.type = 'button';
      var upIcon = svgEl('svg', { viewBox: '0 0 24 24', 'aria-hidden': 'true' });
      upIcon.appendChild(svgEl('path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12' }));
      uploadBtn.appendChild(upIcon);
      uploadBtn.appendChild(document.createTextNode(' Subir imagen'));
      uploadBtn.addEventListener('click', function() { d.galleryInput.click(); });
      topRow.insertBefore(uploadBtn, topRow.firstChild);

      var toast = el('div', 'bisd__toast');
      toast.hidden = true;
      pane.appendChild(toast);

      sheet.appendChild(pane);
      d._scannerEls = {
        pane: pane, video: video, canvas: canvas, viewport: viewport,
        progress: progressTrack.firstChild, perm: permState,
        permTitle: permTitle, permMsg: permMsg, permBtn: permBtn, toast: toast,
      };
      // Shutter sound is created fresh on each capture for reliable replay
    };

    d._showToast = function(text) {
      var t = d._scannerEls && d._scannerEls.toast;
      if (!t) return;
      t.textContent = text;
      t.hidden = false;
      clearTimeout(d._toastTimer);
      d._toastTimer = setTimeout(function() { t.hidden = true; }, 3500);
    };

    // ── Camera lifecycle ──
    d._tryStartCamera = function(explicit) {
      if (d.stream) return;
      // Guard against re-entry: the first call's getUserMedia may still
      // be awaiting the native permission prompt. Re-firing it would race
      // and produce a NotReadableError when one stream attaches and the
      // other is rejected.
      if (d._cameraStarting) return;
      d._cameraStarting = true;
      var els = d._scannerEls;
      els.perm.hidden = true;

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        d._cameraStarting = false;
        d._showPermPanel('Cámara no disponible', 'Tu navegador no soporta acceso a la cámara. Usa el botón "Subir" para enviar una foto.');
        return;
      }

      navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      }).then(function(s) {
        d._cameraStarting = false;
        d.stream = s;
        els.video.srcObject = s;
        var p = els.video.play();
        if (p && p.catch) p.catch(function() {});
        els.perm.hidden = true;
        els.viewport.classList.add('is-live');
        d._startScanLoop();
      }).catch(function(err) {
        d._cameraStarting = false;
        var name = err && err.name;
        var title = 'Cámara no disponible';
        var msg = 'No se pudo iniciar la cámara. Usa el botón "Subir" para enviar una foto.';
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          title = 'Permiso de cámara denegado';
          msg = 'Habilita el acceso a la cámara desde la configuración del navegador, o usa "Subir" para enviar una foto.';
        } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
          title = 'Sin cámara';
          msg = 'No se encontró una cámara en este dispositivo. Usa el botón "Subir" para enviar una foto.';
        } else if (name === 'NotReadableError' || name === 'TrackStartError') {
          title = 'Cámara ocupada';
          msg = 'La cámara está siendo usada por otra aplicación. Cierra la otra app e inténtalo de nuevo.';
        }
        d._showPermPanel(title, msg);
      });
    };

    d._showPermPanel = function(title, message) {
      var els = d._scannerEls;
      if (!els) return;
      if (title) els.permTitle.textContent = title;
      if (message) els.permMsg.textContent = message;
      els.perm.hidden = false;
    };

    d._startScanLoop = function() {
      // No more auto-scan loop. Camera starts, user taps to scan.
      // Pulse the brackets once to signal "ready"
      d._setBrackets('pulse');
      setTimeout(function() { d._setBrackets(null); }, 800);
    };

    d._stopScanLoop = function() {
      if (d.scanTimer) { clearInterval(d.scanTimer); d.scanTimer = null; }
    };

    d._setBrackets = function(cls) {
      if (!d._brackets) return;
      d._brackets.forEach(function(c) {
        c.classList.remove('pulse', 'no-match', 'scanning');
      });
      if (cls) d._brackets.forEach(function(c) { c.classList.add(cls); });
    };

    d._stopCamera = function() {
      d._stopScanLoop();
      var v = d._scannerEls && d._scannerEls.video;
      if (v) v.srcObject = null;
      if (d.stream) {
        d.stream.getTracks().forEach(function(t) { t.stop(); });
        d.stream = null;
      }
    };

    d._setProgress = function(running) {
      var p = d._scannerEls && d._scannerEls.progress;
      if (!p) return;
      p.classList.toggle('is-running', !!running);
      // Show/hide scanning feedback overlay + dim reticle
      var vp = d._scannerEls && d._scannerEls.viewport;
      if (vp) {
        var fb = vp.querySelector('.bisd__scan-feedback');
        if (fb) fb.classList.toggle('visible', !!running);
        var ret = vp.querySelector('.bisd__reticle');
        if (ret) ret.style.opacity = running ? '0.2' : '';
      }
    };

    d._captureFrame = function(force) {
      if (d.inflight) return;
      var els = d._scannerEls;
      if (!els || !d.stream || !els.video.videoWidth) return;
      d.inflight = true;
      d._setBrackets('scanning');
      // Hide caption on first scan
      if (d._caption && !d._caption.hidden) {
        d._caption.hidden = true;
      }
      // Disable tap overlay during analysis
      var tap = els.viewport.querySelector('.bisd__tap-overlay');
      if (tap) tap.classList.add('disabled');
      // Capture feedback: brief white flash + camera shutter sound
      var flash = els.viewport.querySelector('.bisd__flash');
      if (flash) { flash.classList.add('active'); setTimeout(function() { flash.classList.remove('active'); }, 200); }

      // Play shutter sound — recreate each time for reliable replay
      var shutterMs = 0;
      try {
        var snd = new Audio('https://cdn.shopify.com/s/files/1/0196/2088/8676/t/49/assets/shutter.mp3?v=1777480675');
        snd.volume = 0.7;
        snd.play().catch(function() {});
        // Show "Analizando" after shutter finishes (use duration or 300ms fallback)
        shutterMs = 300;
        snd.addEventListener('loadedmetadata', function() {
          if (snd.duration && isFinite(snd.duration)) shutterMs = Math.ceil(snd.duration * 1000);
        });
      } catch(e) {}

      // Delay the "Analizando" overlay until after shutter sound
      setTimeout(function() {
        if (!d.inflight) return; // cancelled while waiting
        d._setProgress(true);
      }, shutterMs);
      var token = ++d._searchToken;
      var c = els.canvas, v = els.video;
      var ctx = c.getContext('2d');
      ctx.drawImage(v, 0, 0, c.width, c.height);
      c.toBlob(function(blob) {
        if (!blob) { d.inflight = false; d._setProgress(false); d._setBrackets(null); if (tap) tap.classList.remove('disabled'); return; }
        d._postVision(blob).then(function(data) {
          // Vision results come back fully hydrated (formatProduct) when
          // the server receives image-only — skip hydration to avoid
          // passing already-display-ready data through Storefront GraphQL.
          // Check: vision responses include `identification` from Gemini.
          if (data && data.identification) {
            return data; // vision mode — already has handle/title/image
          }
          var results = (data && data.results) || [];
          var alreadyHydrated = results.length && results.every(function(r) { return r && r.handle; });
          if (alreadyHydrated) {
            return data; // already hydrated
          }
          return d._hydrate(data);
        }).then(function(data) {
          d.inflight = false; d._setProgress(false);
          if (token !== d._searchToken) return;
          if (data && data.results && data.results.length) {
            d._setBrackets(null);
            d._onMatches(data);
          } else {
            // No match — flash brackets red, show toast, return to ready
            d._setBrackets('no-match');
            d._showToast('No pudimos identificar ese perfume. Intenta de nuevo.');
            setTimeout(function() { d._setBrackets('pulse'); }, 600);
            setTimeout(function() { d._setBrackets(null); }, 1200);
            if (tap) tap.classList.remove('disabled');
            // Track failed scan
            if (window.PriveBrainIntel && typeof window.PriveBrainIntel._track === 'function') {
              try { window.PriveBrainIntel._track('image_search_fail', {}); } catch (e) {}
            }
          }
        }).catch(function(err) {
          d.inflight = false; d._setProgress(false);
          d._setBrackets(null);
          if (tap) tap.classList.remove('disabled');
          var msg = err && err.message;
          if (msg === 'hydrate-no-handle' || msg === 'hydrate-unavailable') {
            d._stopScanLoop();
            d._showToast('Falta token Storefront. Configura shop.metafields.prive.storefront_access_token.');
          }
        });
      }, 'image/jpeg', 0.7);
    };

    // PRV-250 unified endpoint. Search results come server-side hydrated
    // from prive-search (Shopify Admin API 2026-04) — display data and
    // fragrance profile embedded in each result. No client-side hydration.
    d._postUnifiedSearch = function(formOrBody) {
      var ctrl = new AbortController();
      var to = setTimeout(function() { ctrl.abort(); }, 12000);
      var init = { method: 'POST', signal: ctrl.signal };
      // Attach visitor capability for taste compatibility scoring (TU GUSTO)
      var capability = _brainCapability();
      if (formOrBody instanceof FormData) {
        if (capability) formOrBody.append('vid', capability.vid);
        if (capability) formOrBody.append('vid_token', capability.vid_token);
        init.body = formOrBody;
      } else {
        var body = Object.assign({}, formOrBody || {});
        if (capability) body.vid = capability.vid;
        if (capability) body.vid_token = capability.vid_token;
        init.headers = { 'Content-Type': 'application/json' };
        init.body = JSON.stringify(body);
      }
      return fetch(API + '/api/search', init)
        .then(function(r) { clearTimeout(to); if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
    };
    d._postVision = function(blob) {
      var fd = new FormData();
      fd.append('image', blob, 'frame.jpg');
      fd.append('limit', String(_maxMatches));
      return d._postUnifiedSearch(fd);
    };
    // The /api/search endpoint returns server-side hydrated results from
    // prive-search (Shopify Admin API). Each result includes display data
    // (title, handle, vendor, price, image) plus the full fragrance profile
    // from the custom.fragrance_data metaobject. If hydration is missing
    // (no title on first result), the pipeline is broken — fail loud.
    d._hydrate = function(data) {
      var results = (data && data.results) || [];
      if (!results.length) return Promise.resolve(data);
      var first = results[0];
      if (!first || !first.title) {
        return Promise.reject(new Error('hydrate-unavailable'));
      }
      return Promise.resolve(data);
    };

    // ── Upload path: OCR → text search, vision fallback ──
    d._handleUpload = function(file) {
      d._setProgress(true);
      d.uploadMode = true;
      var token = ++d._searchToken;
      resizeForUpload(file).then(function(resized) {
        return getOcrWorker().then(function(worker) {
          return worker.recognize(resized.canvas || resized.blob);
        }).then(function(ocrResult) {
          var raw = (ocrResult && ocrResult.data && ocrResult.data.text) || '';
          var ocr = normalizeOcr(raw);
          // Always send image so Gemini vision + FTS both run in parallel.
          // If OCR found text, also include it — dual signal = better accuracy.
          var fd = new FormData();
          fd.append('image', resized.blob, 'upload.jpg');
          fd.append('limit', String(_maxMatches));
          if (isUsableOcr(ocr.tokens)) {
            fd.append('q', ocr.text);
          }
          return d._postUnifiedSearch(fd);
        }).catch(function() { return d._postVision(resized.blob); });
      }).then(function(data) {
        return d._hydrate(data);
      }).then(function(data) {
        d._setProgress(false);
        if (token !== d._searchToken) return;
        if (data && data.results && data.results.length) {
          d._onMatches(data);
        } else {
          d._showToast('No pudimos identificar ese perfume. Intenta con otra foto.');
        }
      }).catch(function(err) {
        d._setProgress(false);
        var status = err && err.message ? String(err.message) : '';
        var msg = 'No se pudo analizar la imagen.';
        if (status === 'hydrate-unavailable') {
          msg = 'El servidor no respondió con datos del producto. Intenta de nuevo.';
        }
        else if (status === 'HTTP 413') msg = 'La imagen es demasiado grande.';
        else if (status === 'HTTP 429') msg = 'Demasiadas búsquedas. Espera un momento.';
        else if (status === 'HTTP 503' || status === 'HTTP 502') msg = 'El servicio está reiniciando.';
        d._showToast(msg);
      });
    };

    // ── Match handling ──
    d._onMatches = function(data) {
      d._stopCamera();
      d.matches = (data.results || []).slice(0, _maxMatches);
      if (!d.matches.length) return;
      d.activeMatch = 0;
      d.activeTab = 0;
      d._lastSearchData = data || {};
      // Save to sessionStorage so login redirect can restore the scan
      try { sessionStorage.setItem('prive_scan_result', JSON.stringify({ results: d.matches, identification: data.identification || null })); } catch(e) {}
      // Normalize Shopify profile data into shape the render functions expect.
      // Keyed by match index (not handle) — handles are for URLs only.
      d.profileCache = {};
      for (var i = 0; i < d.matches.length; i++) {
        if (d.matches[i].profile) {
          d.profileCache[i] = _normalizeProfile(d.matches[i]);
        }
      }
      d._renderResult();
      d._setMode('result');
      d._loadProfileFor(0);
      if (window.PriveBrainIntel && typeof window.PriveBrainIntel.trackImageSearch === 'function') {
        try { window.PriveBrainIntel.trackImageSearch(_imageSearchIntelPayload(data, d.matches, 0, 'results_shown')); } catch (e) {}
      }
    };

    // Profile data is embedded in the search response from server-side
    // Shopify Admin API hydration (custom.fragrance_data metaobject).
    // No separate fetch needed — brain gives IDs only, Shopify gives everything.
    d._loadProfileFor = function(idx) {
      var match = d.matches[idx];
      if (!match) return;
      var profile = d.profileCache[idx];
      if (d.activeMatch !== idx) return;
      d._renderTabContent(profile || null);
    };

    d._trackImageSearchSelect = function(action) {
      if (!window.PriveBrainIntel || typeof window.PriveBrainIntel.trackImageSearchSelect !== 'function') return;
      try {
        window.PriveBrainIntel.trackImageSearchSelect(_imageSearchIntelPayload(d._lastSearchData || {}, d.matches || [], d.activeMatch || 0, action || 'select'));
      } catch (e) {}
    };

    // ── Result screen (B2) ──
    d._renderResult = function() {
      var existing = sheet.querySelector('.bisd__result');
      if (existing) existing.parentNode.removeChild(existing);
      var pane = el('div', 'bisd__result');

      var topRow = el('div', 'bisd__result-top');
      var back = el('button', 'bisd__close');
      back.type = 'button';
      back.setAttribute('aria-label', 'Volver al escáner');
      var backSvg = svgEl('svg', { viewBox: '0 0 24 24', 'aria-hidden': 'true' });
      backSvg.appendChild(svgEl('path', { d: 'M18 6 6 18M6 6l12 12' }));
      back.appendChild(backSvg);
      back.addEventListener('click', function() { d._returnToScanner(); });
      topRow.appendChild(back);
      var label = d.matches.length === 1 ? '1 coincidencia' : (d.matches.length + ' coincidencias');
      topRow.appendChild(el('div', 'bisd__result-count', label));
      var spacer = el('div', 'bisd__result-spacer');
      topRow.appendChild(spacer);
      pane.appendChild(topRow);

      var heroOuter = el('div', 'bisd__hero-outer');
      var heroTrack = el('div', 'bisd__hero-track');
      d.matches.forEach(function(m, i) {
        var card = el('div', 'bisd__hero-card');
        if (m.match_score) {
          var pct = Math.round((m.match_score > 1 ? m.match_score : m.match_score * 100));
          card.appendChild(el('div', 'bisd__hero-badge', pct + '%'));
        }
        var imgWrap = el('div', 'bisd__hero-img');
        if (m.image) {
          var img = document.createElement('img');
          img.src = m.image;
          img.alt = m.title || '';
          img.loading = 'lazy';
          imgWrap.appendChild(img);
        }
        card.appendChild(imgWrap);
        var info = el('div', 'bisd__hero-info');
        if (m.vendor) info.appendChild(el('div', 'bisd__hero-vendor', m.vendor));
        // Strip the vendor prefix and the trailing "EDP / 105 ml" tail
        // so the headline reads cleanly.
        var clean = _cleanTitle(m.title, m.vendor);
        info.appendChild(el('div', 'bisd__hero-title', clean || m.title || ''));
        var metaParts = [];
        var conc = m.concentration || _concentrationFromTitle(m.title);
        if (conc) {
          // Compact form for the meta line ("EDP" reads better in a
          // small chip than "Eau de Parfum").
          metaParts.push(conc.replace(/Eau de Parfum/i, 'EDP').replace(/Eau de Toilette/i, 'EDT').replace(/Eau de Cologne/i, 'EDC').toUpperCase());
        }
        var size = m.bottle_size || _bottleSizeFromTitle(m.title);
        if (size) metaParts.push(size.toUpperCase());
        var gen = _translateGenero(m.gender);
        if (gen) metaParts.push(gen.toUpperCase());
        if (metaParts.length) info.appendChild(el('div', 'bisd__hero-meta', metaParts.join(' · ')));
        card.appendChild(info);
        heroTrack.appendChild(card);
      });
      heroOuter.appendChild(heroTrack);
      pane.appendChild(heroOuter);

      var dots = el('div', 'bisd__dots');
      var prevBtn = null;
      var nextBtn = null;
      if (d.matches.length > 1) {
        prevBtn = el('button', 'bisd__hero-nav bisd__hero-nav--prev');
        prevBtn.type = 'button';
        prevBtn.setAttribute('aria-label', 'Coincidencia anterior');
        prevBtn.appendChild(_navArrow('prev'));
        prevBtn.addEventListener('click', function() { d._setMatch(d.activeMatch - 1); });
        dots.appendChild(prevBtn);
      }
      d.matches.forEach(function(_, i) {
        var dot = el('button', 'bisd__dot' + (i === 0 ? ' is-active' : ''));
        dot.type = 'button';
        dot.setAttribute('aria-label', 'Coincidencia ' + (i + 1));
        dot.addEventListener('click', function() { d._setMatch(i); });
        dots.appendChild(dot);
      });
      if (d.matches.length > 1) {
        nextBtn = el('button', 'bisd__hero-nav bisd__hero-nav--next');
        nextBtn.type = 'button';
        nextBtn.setAttribute('aria-label', 'Coincidencia siguiente');
        nextBtn.appendChild(_navArrow('next'));
        nextBtn.addEventListener('click', function() { d._setMatch(d.activeMatch + 1); });
        dots.appendChild(nextBtn);
      }
      pane.appendChild(dots);

      var tabs = el('div', 'bisd__tabs');
      var tabLabels = ['DETALLES', 'NOTAS', 'PERFIL'];
      tabLabels.forEach(function(lbl, i) {
        var t = el('button', 'bisd__tab' + (i === 0 ? ' is-active' : ''));
        t.type = 'button';
        t.textContent = lbl;
        t.addEventListener('click', function() { d._setTab(i); });
        tabs.appendChild(t);
      });
      pane.appendChild(tabs);

      var content = el('div', 'bisd__tab-content');
      content.appendChild(el('div', 'bisd__tab-loading', 'Cargando…'));
      pane.appendChild(content);

      var ctaWrap = el('div', 'bisd__cta-wrap');
      var cta = el('a', 'bisd__cta');
      cta.textContent = 'Ver en Privé →';
      cta.addEventListener('click', function() { d._trackImageSearchSelect('cta_click'); });
      ctaWrap.appendChild(cta);
      pane.appendChild(ctaWrap);

      sheet.appendChild(pane);
      d._resultEls = { pane: pane, heroTrack: heroTrack, prevBtn: prevBtn, nextBtn: nextBtn, dots: dots, tabs: tabs, content: content, cta: cta };
      d._wireHeroSwipe();
      d._updateHero();
      d._updateCta();
    };

    d._returnToScanner = function() {
      d.matches = [];
      d.profileCache = {};
      d.inflight = false;
      d._setProgress(false);
      d._setMode('scanner');
      var existing = sheet.querySelector('.bisd__result');
      if (existing) existing.parentNode.removeChild(existing);
      d._resultEls = null;
      if (d.uploadMode) {
        d.uploadMode = false;
        return;
      }
      // Re-enable tap overlay so user can scan again
      var els = d._scannerEls;
      if (els) {
        var tap = els.viewport && els.viewport.querySelector('.bisd__tap-overlay');
        if (tap) tap.classList.remove('disabled');
      }
      if (d.stream) {
        d._startScanLoop();
      } else {
        d._tryStartCamera(true);
      }
    };

    d._setMatch = function(i) {
      if (i < 0 || i >= d.matches.length) return;
      d.activeMatch = i;
      d._updateHero();
      d._updateCta();
      var els = d._resultEls;
      if (!els) return;
      Array.prototype.forEach.call(els.dots.querySelectorAll('.bisd__dot'), function(dot, idx) {
        dot.classList.toggle('is-active', idx === i);
      });
      els.content.textContent = '';
      els.content.appendChild(el('div', 'bisd__tab-loading', 'Cargando…'));
      d._loadProfileFor(i);
    };

    d._updateHero = function() {
      var els = d._resultEls;
      if (!els) return;
      els.heroTrack.style.transform = 'translate3d(' + (-d.activeMatch * 100) + '%, 0, 0)';
      if (els.prevBtn) els.prevBtn.disabled = d.activeMatch <= 0;
      if (els.nextBtn) els.nextBtn.disabled = d.activeMatch >= d.matches.length - 1;
    };

    d._updateCta = function() {
      var els = d._resultEls;
      if (!els) return;
      var match = d.matches[d.activeMatch] || {};
      els.cta.href = match.url || ('https://priveperfumes.com/products/' + (match.handle || ''));
    };

    d._setTab = function(i) {
      d.activeTab = i;
      var els = d._resultEls;
      if (!els) return;
      Array.prototype.forEach.call(els.tabs.children, function(t, idx) {
        t.classList.toggle('is-active', idx === i);
      });
      var match = d.matches[d.activeMatch];
      var profile = match && d.profileCache[d.activeMatch];
      d._renderTabContent(profile);
    };

    d._renderTabContent = function(profile) {
      var els = d._resultEls;
      if (!els) return;
      var match = d.matches[d.activeMatch] || {};
      var content = els.content;
      content.textContent = '';

      if (d.activeTab === 0) {
        // DETALLES — commerce + ambient signals consolidated. Rating/
        // sillage live here too because users were missing them when
        // they were tucked behind a separate tab.
        content.appendChild(_priceBlock(match));
        _appendIf(content, _specGrid(match, profile));
        _appendIf(content, _ratingBlock(match, profile));
        _appendIf(content, _opinionDistribution(profile));
        _appendIf(content, _sillageBlock(profile));
        _appendIf(content, _climaBlock(profile));
        _appendIf(content, _ocasionBlock(profile));
        _appendIf(content, _duracionBlock(profile));
      } else if (d.activeTab === 1) {
        // NOTAS — scent breakdown.
        var nb = _notasBlock(profile);
        var ab = _acordesBlock(profile);
        if (nb) content.appendChild(nb);
        if (ab) content.appendChild(ab);
        if (!nb && !ab) {
          content.appendChild(el('div', 'bisd__empty', 'Sin notas disponibles.'));
        }
      } else if (d.activeTab === 2) {
        // PERFIL — visitor taste intelligence.
        content.appendChild(_profileTab(match));
      }
    };

    d._wireHeroSwipe = function() {
      // Dispose previous listeners before wiring new ones
      if (d._disposeHeroSwipe) d._disposeHeroSwipe();
      var els = d._resultEls;
      if (!els || d.matches.length <= 1) return;
      var startX = 0, dx = 0, dragging = false, w = 0;
      var track = els.heroTrack;
      function onDown(e) {
        var pt = e.touches ? e.touches[0] : e;
        startX = pt.clientX;
        dx = 0;
        dragging = true;
        w = track.parentNode.getBoundingClientRect().width || 1;
        track.style.transition = 'none';
      }
      function onMove(e) {
        if (!dragging) return;
        var pt = e.touches ? e.touches[0] : e;
        dx = pt.clientX - startX;
        var base = -d.activeMatch * 100;
        track.style.transform = 'translate3d(calc(' + base + '% + ' + dx + 'px), 0, 0)';
      }
      function onUp() {
        if (!dragging) return;
        dragging = false;
        track.style.transition = '';
        if (Math.abs(dx) > w * 0.18) {
          var next = d.activeMatch + (dx < 0 ? 1 : -1);
          next = Math.max(0, Math.min(d.matches.length - 1, next));
          d._setMatch(next);
        } else {
          d._updateHero();
        }
      }
      track.addEventListener('touchstart', onDown, { passive: true });
      track.addEventListener('touchmove', onMove, { passive: true });
      track.addEventListener('touchend', onUp);
      track.addEventListener('mousedown', onDown);
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      d._disposeHeroSwipe = function() {
        track.removeEventListener('touchstart', onDown);
        track.removeEventListener('touchmove', onMove);
        track.removeEventListener('touchend', onUp);
        track.removeEventListener('mousedown', onDown);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        d._disposeHeroSwipe = null;
      };
    };

    return d;
  }

  function _ensureDrawer() {
    if (!_drawer) _drawer = _buildDrawer();
    return _drawer;
  }

  var BrainImageSearch = class extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('.brain-image-search__btn')) return;
      var btn = el('button', 'brain-image-search__btn');
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Identificar perfume con la cámara');
      btn.title = 'Identificar perfume con la cámara';
      var cameraSvg = svgEl('svg', { width: '20', height: '20', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '1.5', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' });
      cameraSvg.appendChild(svgEl('path', { d: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z' }));
      cameraSvg.appendChild(svgEl('circle', { cx: '12', cy: '13', r: '4' }));
      btn.appendChild(cameraSvg);
      btn.addEventListener('click', function() { _ensureDrawer().show(); });
      this.appendChild(btn);
    }
  };
  customElements.define('brain-image-search', BrainImageSearch);

  // ── WhatsApp Product Inquiry ────────────────────────────────────────

  // HulkApps wishlist is a Shopify app block rendered in a sibling container,
  // outside of where the WhatsApp button mounts. To show them side-by-side we
  // build a row wrapper here and hoist the wishlist anchor into it when it
  // appears. If it never appears (app disabled / errored), the WA button
  // just stretches alone.
  function hoistWishlistInto(row) {
    var wl = document.querySelector('.wishlist-btn');
    if (wl && !row.contains(wl)) {
      var slot = row.querySelector('.brain-actions__wishlist-slot');
      if (slot) slot.appendChild(wl); else row.insertBefore(wl, row.firstChild);
      return true;
    }
    return false;
  }

  var BrainWhatsApp = class extends HTMLElement {
    connectedCallback() {
      var title = (this.getAttribute('product-title') || '').replace(/[\r\n]/g, ' ').substring(0, 200);
      var url = this.getAttribute('product-url') || window.location.href;

      var row = el('div', 'brain-actions');
      var wlSlot = el('div', 'brain-actions__wishlist-slot');
      row.appendChild(wlSlot);

      var link = el('a', 'brain-whatsapp brain-actions__whatsapp');
      link.href = _whatsAppProductHref(title, url);
      link.target = '_blank';
      link.rel = 'noopener';
      link.setAttribute('aria-label', 'Consultar por WhatsApp');
      var waHandle = (url || '').split('/products/')[1];
      if (waHandle) waHandle = waHandle.split('?')[0];
      link.addEventListener('click', function(event) {
        var intel = window.PriveBrainIntel;
        var capability = _brainCapability();
        var renewal = null;
        var popup = null;
        var handoffFresh = capability
          && /^[A-Za-z0-9_-]{43}$/.test(capability.handoff_ticket || '')
          && Number(capability.handoff_expires_at) > Date.now() + 60000;
        if (!handoffFresh && intel && typeof intel.ensureFreshPurposeTickets === 'function') {
          if (event && event.preventDefault) event.preventDefault();
          try {
            popup = window.open('about:blank', '_blank');
            if (popup) popup.opener = null;
          } catch (e) {}
          renewal = intel.ensureFreshPurposeTickets();
        } else {
          link.href = _whatsAppProductHref(title, url);
        }
        if (!intel) return;
        // Read product context from DOM at click time so price reflects the
        // currently selected variant (not a static Liquid attribute).
        var tapTitle = null, tapVendor = null, tapPrice = null;
        var titleEl = document.querySelector('.product-meta__title');
        var vendorEl = document.querySelector('.product-meta__vendor');
        var priceEl = document.querySelector('.price--large');
        if (titleEl) tapTitle = titleEl.textContent.trim();
        if (vendorEl) tapVendor = vendorEl.textContent.trim();
        if (priceEl) {
          var p = parseFloat(priceEl.textContent.replace(/[^\d.,]/g, '').replace(',', ''));
          if (!isNaN(p)) tapPrice = p;
        }
        intel.trackWhatsAppTap(waHandle, tapTitle, tapVendor, tapPrice);
        if (renewal) {
          renewal.then(function(ready) {
            if (!ready) {
              try { if (popup) popup.close(); } catch (e) {}
              return;
            }
            var renewedHref = _whatsAppProductHref(title, url);
            link.href = renewedHref;
            try {
              if (popup) popup.location.href = renewedHref;
              else if (window.location.assign) window.location.assign(renewedHref);
              else window.location.href = renewedHref;
            } catch (e) {}
          }).catch(function() {
            try { if (popup) popup.close(); } catch (e) {}
          });
        }
      });
      var waSvg = svgEl('svg', {width:'18',height:'18',viewBox:'0 0 24 24',fill:'currentColor'});
      waSvg.appendChild(svgEl('path', {d:'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z'}));
      link.appendChild(waSvg);
      link.appendChild(document.createTextNode(' '));
      var waLabel = document.createElement('span');
      waLabel.textContent = 'Consultar por WhatsApp';
      link.appendChild(waLabel);
      row.appendChild(link);
      this.appendChild(row);

      if (hoistWishlistInto(row)) return;
      var mo = new MutationObserver(function() {
        if (hoistWishlistInto(row)) mo.disconnect();
      });
      mo.observe(document.body, { childList: true, subtree: true });
      // Stop listening after 8s — HulkApps either rendered by now or it isn't coming.
      setTimeout(function() { mo.disconnect(); }, 8000);
    }
  };
  customElements.define('brain-whatsapp-inquiry', BrainWhatsApp);

  _installEmptyJudgeMeReviewHider();

  // ── Shopify Storefront Hydration (PRV-254) ────────────────────────
  // After brain returns IDs, call Shopify Storefront API to fetch
  // fresh display data (prices, promos, inventory).

  var BRAIN_NS = (window.PriveBrain = window.PriveBrain || {});

  document.dispatchEvent(new CustomEvent('prive:brain:ready'));
})();
